import React, { useEffect, useRef, useState } from "react";
import { useLiveEdit } from "../../lib/liveEdit";
import api from "../../lib/api";
import { Upload, Video as VideoIcon, Check, X, Loader2, Pencil } from "lucide-react";

/* ------------------------- Text / Rich Text ------------------------- */
export function EditableText({
  path,
  as: Tag = "span",
  className = "",
  children,
  multiline = false,
  placeholder = "",
}) {
  const { editMode, isAdmin, get, save } = useLiveEdit();
  const fallback = typeof children === "string" ? children : "";
  const value = get(path, fallback);
  const ref = useRef(null);
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);

  if (!editMode || !isAdmin) {
    return <Tag className={className}>{value || fallback || placeholder}</Tag>;
  }

  const startEdit = (e) => {
    e.stopPropagation?.();
    setEditing(true);
    setTimeout(() => {
      const el = ref.current;
      if (!el) return;
      el.focus();
      const range = document.createRange();
      range.selectNodeContents(el);
      range.collapse(false);
      const sel = window.getSelection();
      sel.removeAllRanges();
      sel.addRange(range);
    }, 0);
  };

  const commit = async () => {
    if (!ref.current) return;
    const next = (ref.current.innerText || "").trim();
    if (next === (value || fallback)) {
      setEditing(false);
      return;
    }
    setBusy(true);
    try {
      await save(path, multiline ? "richtext" : "text", next);
    } catch (e) {
      alert("Save failed: " + (e.response?.data?.detail || e.message));
    } finally {
      setBusy(false);
      setEditing(false);
    }
  };

  const cancel = () => {
    if (ref.current) ref.current.innerText = value || fallback;
    setEditing(false);
  };

  return (
    <Tag
      ref={ref}
      contentEditable={editing}
      suppressContentEditableWarning
      onClick={!editing ? startEdit : undefined}
      onBlur={editing ? commit : undefined}
      onKeyDown={(e) => {
        if (!editing) return;
        if (e.key === "Enter" && !multiline) {
          e.preventDefault();
          ref.current?.blur();
        }
        if (e.key === "Escape") {
          e.preventDefault();
          cancel();
          ref.current?.blur();
        }
      }}
      className={
        className +
        " relative rounded transition " +
        (editing
          ? "outline outline-2 outline-yellow-400 bg-yellow-50/40 px-1"
          : "hover:outline hover:outline-2 hover:outline-yellow-400 hover:outline-offset-2 cursor-text")
      }
      data-editable-path={path}
      data-testid={`editable-${path}`}
    >
      {value || fallback}
      {busy && (
        <Loader2 className="inline h-3.5 w-3.5 ml-1 animate-spin text-yellow-500" />
      )}
    </Tag>
  );
}

/* ------------------------- Image (with upload) ------------------------- */
export function EditableImage({
  path,
  src: defaultSrc,
  alt = "",
  className = "",
  imgClassName = "",
  wrapperStyle,
}) {
  const { editMode, isAdmin, get, save } = useLiveEdit();
  const src = get(path, defaultSrc);
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const res = await api.post("/upload", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      await save(path, "image", res.data.url);
    } catch (err) {
      alert("Upload failed: " + (err.response?.data?.detail || err.message));
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const showOverlay = editMode && isAdmin;

  return (
    <div
      className={"relative group/edit-img " + className}
      style={wrapperStyle}
      data-editable-path={path}
      data-testid={`editable-image-${path}`}
    >
      <img
        src={src}
        alt={alt}
        className={imgClassName || "w-full h-full object-cover"}
      />
      {showOverlay && (
        <div className="pointer-events-none absolute inset-0 rounded-[inherit] ring-2 ring-yellow-400/0 group-hover/edit-img:ring-yellow-400 transition">
          <div className="pointer-events-auto absolute inset-0 flex items-center justify-center bg-black/50 opacity-0 group-hover/edit-img:opacity-100 transition rounded-[inherit]">
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              disabled={uploading}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-yellow-400 text-[#0B2A6B] font-bold shadow-lg hover:bg-yellow-500 disabled:opacity-60"
              data-testid={`upload-btn-${path}`}
            >
              {uploading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Upload className="h-4 w-4" />
              )}
              {uploading ? "Uploading..." : "Change Image"}
            </button>
            <input
              ref={inputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={handleUpload}
            />
          </div>
        </div>
      )}
    </div>
  );
}

/* ------------------------- Video (YouTube) ------------------------- */
function extractYoutubeId(input) {
  const raw = (input || "").trim();
  if (!raw) return "";
  const m = raw.match(/(?:v=|youtu\.be\/|embed\/|shorts\/)([\w-]{11})/);
  return m ? m[1] : raw;
}

export function EditableVideo({ path, youtubeId: defaultId, className = "", title = "video" }) {
  const { editMode, isAdmin, get, save } = useLiveEdit();
  const value = get(path, defaultId);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setDraft(value);
  }, [value]);

  const submit = async () => {
    const id = extractYoutubeId(draft);
    if (!id) {
      alert("Invalid YouTube URL or ID");
      return;
    }
    setBusy(true);
    try {
      await save(path, "video", id);
      setEditing(false);
    } catch (err) {
      alert("Save failed: " + (err.response?.data?.detail || err.message));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={"relative " + className} data-editable-path={path} data-testid={`editable-video-${path}`}>
      <div className="relative aspect-video rounded-2xl overflow-hidden bg-black">
        <iframe
          key={value}
          src={`https://www.youtube.com/embed/${value}`}
          className="absolute inset-0 w-full h-full"
          title={title}
          frameBorder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />
      </div>
      {editMode && isAdmin && (
        <div className="absolute top-3 right-3 z-30">
          {!editing ? (
            <button
              type="button"
              onClick={() => {
                setDraft(value);
                setEditing(true);
              }}
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-yellow-400 text-[#0B2A6B] font-bold shadow-lg text-xs"
              data-testid={`edit-video-btn-${path}`}
            >
              <VideoIcon className="h-4 w-4" /> Change Video
            </button>
          ) : (
            <div className="bg-white p-2 rounded-lg shadow-xl flex items-center gap-2 border border-slate-200">
              <input
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                placeholder="YouTube URL or 11-char ID"
                className="text-sm px-2 py-1 border rounded w-64 text-slate-800"
                autoFocus
              />
              <button
                onClick={submit}
                disabled={busy}
                className="p-1.5 bg-green-500 text-white rounded hover:bg-green-600 disabled:opacity-60"
                title="Save"
              >
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
              </button>
              <button
                onClick={() => setEditing(false)}
                className="p-1.5 bg-slate-200 text-slate-700 rounded hover:bg-slate-300"
                title="Cancel"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ------------------------- Small "Edit" chip helper ------------------------- */
export function EditableHint({ children }) {
  const { editMode, isAdmin } = useLiveEdit();
  if (!editMode || !isAdmin) return null;
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-yellow-400 text-[#0B2A6B] text-[10px] font-bold uppercase tracking-wider">
      <Pencil className="h-3 w-3" />
      {children}
    </span>
  );
}
